import {
  Accordion,
  Box,
  Columns,
  Container,
  Divider,
  Heading,
  Stack,
  Text,
  Visible,
} from '@spaceship-fspl/components';
import { marginBottom, marginTop } from '@spaceship-fspl/styles';
import { TrackingCategory } from '@spaceship-fspl/tracking';
import { PanelSection } from 'components';
import React from 'react';
import styled from 'styled-components';

export interface FaqPanelProps {
  id?: string | null;
  title?: string | null;
  tagline?: React.ReactNode;
  desktopImage?: string;
  mobileImage?: string;
  faqs?: Array<{
    title?: string | null;
    description?: React.ReactNode;
  }>;
}

export const FaqPanel: React.FC<FaqPanelProps> = ({
  id,
  title,
  tagline,
  desktopImage,
  mobileImage,
  faqs,
}) => {
  const showImages = !!desktopImage && !!mobileImage;

  return (
    <TrackingCategory category="FAQ Panel">
      <PanelSection id={id}>
        <Container>
          <Stack spaceY="xl">
            <Columns
              reverse={{ lg: true }}
              alignX="center"
              alignY={{ lg: 'center' }}
              spaceX={{ lg: 'xxl' }}
              spaceY={{ xs: 'lg', lg: 'none' }}
            >
              <Columns.Column width={{ lg: showImages ? 1 / 2 : 10 / 12 }}>
                <Stack spaceY={{ xs: 'sm', lg: 'md' }}>
                  <Heading
                    component="h2"
                    variant={2}
                    color="neutral.100"
                    align={{ xs: 'center', lg: showImages ? 'left' : 'center' }}
                  >
                    {title}
                  </Heading>

                  <Text
                    variant={1}
                    color="neutral.080"
                    align={{ xs: 'center', lg: showImages ? 'left' : 'center' }}
                    component="div"
                  >
                    {tagline}
                  </Text>
                </Stack>
              </Columns.Column>

              {showImages && (
                <Columns.Column width={{ lg: 1 / 2 }}>
                  <Visible isHidden={{ lg: true }}>
                    <StyledImage src={mobileImage} alt={title ?? ''} />
                  </Visible>

                  <Visible isHidden={{ xs: true, lg: false }}>
                    <StyledImage src={desktopImage} alt={title ?? ''} />
                  </Visible>
                </Columns.Column>
              )}
            </Columns>

            <div>
              {faqs?.map((faq) => (
                <React.Fragment key={faq.title}>
                  <Divider color="neutral.050" />

                  <Accordion variant="faq" title={faq.title}>
                    <Box marginTop="sm" marginBottom="xl">
                      <StyledTextComponent
                        variant={1}
                        color="neutral.080"
                        isBold={false}
                        component="div"
                      >
                        {faq.description}
                      </StyledTextComponent>
                    </Box>
                  </Accordion>
                </React.Fragment>
              ))}

              <Divider color="neutral.050" />
            </div>
          </Stack>
        </Container>
      </PanelSection>
    </TrackingCategory>
  );
};

const StyledImage = styled.img`
  max-width: 100%;
`;

const StyledTextComponent = styled(Text)`
  p,
  ol,
  ul {
    ${marginTop('lg')}
    ${marginBottom('none')}

    &:first-child {
      ${marginTop('none')}
    }
  }

  li {
    ${marginTop('sm')}
  }

  img {
    width: 100%;
  }

  ol ol {
    list-style-type: lower-alpha;
  }
`;
